import React, { useState, useEffect } from 'react';
import Pagelayout from '../../components/Pagelayout';
import {
  Box,
  TextField,
  MenuItem,
  Grid,
  Paper,
  Typography,
  Chip,
  Stack,
  Autocomplete,
  Alert,
  CircularProgress,
  Divider,
  Button,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';
import axios from 'axios';
// currency helpers used across the app
import { filterObjFromList } from '../../../../../Js/generalFunctions';
import { companyProfileInfoForUser } from '../../../../../API/Settings/Company Settings/companySettingsAPI';
import { viewAllCurrencyUpdated } from '../../../../../API/Settings/Currency/CurrencyAPI';
import { 
  generateEOSSSequence, 
  createEOSS, 
  updateEOSS, 
  getEOSSById,
  deleteEOSS,
  postEOSS,
  getEOSSList
} from '../../parollAPIs';
import useEmployeeImages from '../../hooks/useEmployeeImages';
import { 
  listAllEmployees, 
  getAllDepartmentsAPI, 
  getAllDesignationsAPI,
  listPayHeadApi,
  getEmployeeBasicDataAPI
} from '../../parollAPIs';

const CreateEoss = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  // Get currency from company profile (Settings page)
  const companyProfileSlice = useSelector((state) => state.companyProfileSlice);
  const companyProfileData = companyProfileSlice?.userValue || companyProfileSlice?.value;
  // Currency master list (kept by settings page) — do NOT create new array here
  const currencyListUpdated = useSelector((state) => state.viewUpdatedCurrency?.value);

  // Derive a display currency SYMBOL robustly using Intl when possible
  const getCurrencySymbol = () => {
    // currency may live under userValue.profile.currency OR userValue.currency (root)
    const currencyObj =
      companyProfileData?.profile?.currency ??
      companyProfileData?.currency ??
      companyProfileData?.profile?.currencyId ?? // some pages store an id
      companyProfileData?.currencyId;

    // If currency is an object with a symbol, prefer it
    if (currencyObj && typeof currencyObj === 'object') {
      if (currencyObj.symbol) return currencyObj.symbol;
      // If it's a ref object with _id, try to map using currency list
      const byId = currencyObj._id && Array.isArray(currencyListUpdated)
        ? currencyListUpdated.find(c => c?._id === currencyObj._id)
        : null;
      if (byId) return byId.symbol || byId.cc || byId.name || byId.code;
      const code = currencyObj.cc || currencyObj.code || currencyObj.currency || currencyObj.name;
      if (code && typeof code === 'string') {
        try {
          const parts = new Intl.NumberFormat(undefined, { style: 'currency', currency: code, minimumFractionDigits: 0 }).formatToParts(0);
          const sym = parts.find(p => p.type === 'currency')?.value;
          return sym || code;
        } catch (e) {
          return code;
        }
      }
    }

    // If currency is a string directly (could be a code like 'OMR')
    if (typeof currencyObj === 'string' && currencyObj) {
      // If it's an ID, map from list
      const byId = Array.isArray(currencyListUpdated)
        ? currencyListUpdated.find(c => c?._id === currencyObj)
        : null;
      if (byId) return byId.symbol || byId.cc || byId.name || byId.code;
      try {
        const parts = new Intl.NumberFormat(undefined, { style: 'currency', currency: currencyObj, minimumFractionDigits: 0 }).formatToParts(0);
        const sym = parts.find(p => p.type === 'currency')?.value;
        return sym || currencyObj;
      } catch (e) {
        return currencyObj;
      }
    }

    // Fallback
    return '$';
  };

  const [currencySymbol, setCurrencySymbol] = useState('$');
  
  // reactively resolve symbol whenever settings or currency list changes
  useEffect(() => {
    const sym = getCurrencySymbol();
    setCurrencySymbol(sym || '$');
  }, [companyProfileData, currencyListUpdated]);

  // Ensure Settings data is loaded (same pattern used elsewhere in app)
  useEffect(() => {
    viewAllCurrencyUpdated({ status: 0 });
    companyProfileInfoForUser();
  }, []);

  // Prefer symbol from Settings mapping helper if available
  useEffect(() => {
    if (currencyListUpdated && companyProfileData) {
      const mapped = filterObjFromList('_id', currencyListUpdated, 'currency', companyProfileData);
      const preferred = mapped?.symbol || mapped?.cc || mapped?.code || mapped?.name;
      if (preferred) setCurrencySymbol(preferred);
    }
  }, [currencyListUpdated, companyProfileData]);

  // State management
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sequence, setSequence] = useState('');
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [allowances, setAllowances] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [errors, setErrors] = useState({});
  const { EmployeeImage } = useEmployeeImages();
  
  // Reconcile selected employee's designation/department names once master lists arrive
  useEffect(() => {
    if (!selectedEmployee) return;
    let updated = { ...selectedEmployee };

    // Fix designation name if missing but we have an ID and list is available
    if (
      (!updated?.designation?.position && (updated?.designation?._id || updated?.designation)) &&
      Array.isArray(designations) && designations.length
    ) {
      const desigId = updated?.designation?._id || updated?.designation;
      const found = designations.find(d => String(d?._id) === String(desigId));
      if (found) {
        updated.designation = {
          _id: found._id,
          position: found.position || found.designationName || found.name || 'N/A',
        };
      }
    }

    // Fix department name if missing but we have an ID and list is available
    if (
      (!updated?.department?.departmentName && (updated?.department?._id || updated?.department)) &&
      Array.isArray(departments) && departments.length
    ) {
      const deptId = updated?.department?._id || updated?.department;
      const found = departments.find(d => String(d?._id) === String(deptId));
      if (found) {
        updated.department = {
          _id: found._id,
          departmentName: found.departmentName || found.name || 'N/A',
        };
      }
    }

    // Apply update only if something changed
    if (
      updated.designation !== selectedEmployee.designation ||
      updated.department !== selectedEmployee.department
    ) {
      setSelectedEmployee(updated);
    }
  }, [designations, departments, selectedEmployee]);
  // Print functionality using direct window.print
  const handlePrint = () => {
    console.log('🖨️ Opening print window for EOSS');
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>EOSS - ${sequence} - ${selectedEmployee?.staff_name || 'Employee'}</title>
        <style>
          @page { size: A4; margin: 20mm; }
          body { font-family: Arial, sans-serif; font-size: 12px; }
          h1 { text-align: center; font-size: 20px; margin-bottom: 20px; }
          .header { text-align: center; margin-bottom: 30px; }
          .section { margin-bottom: 25px; }
          .section-title { font-size: 14px; font-weight: bold; margin-bottom: 10px; color: #1976d2; }
          .row { display: flex; margin-bottom: 8px; }
          .label { width: 200px; font-weight: bold; }
          .value { flex: 1; }
          .summary { background-color: #f5f5f5; padding: 20px; margin: 20px 0; }
          .summary-row { display: flex; margin-bottom: 10px; }
          .summary-label { width: 200px; font-weight: bold; }
          .summary-value { flex: 1; text-align: right; font-weight: bold; }
          .audit { border-top: 2px solid #ddd; padding-top: 20px; margin-top: 30px; }
          .footer { text-align: center; margin-top: 30px; font-size: 11px; color: #666; }
          .status-posted { color: #155724; font-weight: bold; }
          .status-draft { color: #856404; font-weight: bold; }
        </style>
      </head>
      <body>
        <h1>End of Service Settlement (EOSS)</h1>
        
        <div class="header">
          <p><strong>EOSS Number:</strong> ${sequence}</p>
          <p><strong>Status:</strong> <span class="status-${formData.status?.toLowerCase()}">${formData.status}</span></p>
        </div>
        
        <div class="section">
          <div class="section-title">Employee Information</div>
          <div class="row">
            <div class="label">Employee Name:</div>
            <div class="value">${selectedEmployee?.staff_name || 'N/A'}</div>
          </div>
          <div class="row">
            <div class="label">Employee Code:</div>
            <div class="value">${selectedEmployee?.emp_id || 'N/A'}</div>
          </div>
          <div class="row">
            <div class="label">Department:</div>
            <div class="value">${selectedEmployee?.department?.departmentName || 'N/A'}</div>
          </div>
          <div class="row">
            <div class="label">Designation:</div>
            <div class="value">${selectedEmployee?.designation?.position || 'N/A'}</div>
          </div>
          <div class="row">
            <div class="label">Joining Date:</div>
            <div class="value">${selectedEmployee?.date_of_join ? dayjs(selectedEmployee.date_of_join).format('DD MMM YYYY') : 'N/A'}</div>
          </div>
          <div class="row">
            <div class="label">Monthly Salary:</div>
            <div class="value">${currencySymbol}${selectedEmployee?.monthlySalary || 0}</div>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">Settlement Details</div>
          <div class="row">
            <div class="label">Settlement Type:</div>
            <div class="value">${formData.type}</div>
          </div>
          <div class="row">
            <div class="label">Current Date:</div>
            <div class="value">${formData.currentDate}</div>
          </div>
          <div class="row">
            <div class="label">End Date:</div>
            <div class="value">${formData.endDate}</div>
          </div>
          <div class="row">
            <div class="label">Service Period:</div>
            <div class="value">${formData.serviceYear} years ${formData.serviceMonth} months</div>
          </div>
          <div class="row">
            <div class="label">Calculation Base:</div>
            <div class="value">${formData.calculationBase === 1 ? 'Basic + Allowances' : 'Basic Only'}</div>
          </div>
          <div class="row">
            <div class="label">Monthly Salary:</div>
            <div class="value">${currencySymbol}${selectedEmployee?.monthlySalary || 0}</div>
          </div>
          ${formData.allowances.length > 0 ? `
          <div class="row">
            <div class="label">Allowances:</div>
            <div class="value">${formData.allowances.map(a => `${a.name} (${currencySymbol}${a.amount || 0})`).join(', ')}</div>
          </div>
          ` : ''}
        </div>
        
        <div class="summary">
          <div class="section-title">Settlement Summary</div>
          <div class="summary-row">
            <div class="summary-label">Service Period:</div>
            <div class="summary-value">${formData.serviceYear}y ${formData.serviceMonth}m</div>
          </div>
          <div class="summary-row">
            <div class="summary-label">Per Year:</div>
            <div class="summary-value">${currencySymbol}${formData.settlmentPerYear.toFixed(2)}</div>
          </div>
          <div class="summary-row">
            <div class="summary-label">Total Settlement:</div>
            <div class="summary-value">${currencySymbol}${formData.eosTotal.toFixed(2)}</div>
          </div>
          <div class="summary-row">
            <div class="summary-label">Unsettled Loan:</div>
            <div class="summary-value">${currencySymbol}${formData.unSettledLoan.toFixed(2)}</div>
          </div>
          <div class="summary-row" style="border-top: 2px solid #333; padding-top: 10px;">
            <div class="summary-label">Net Amount:</div>
            <div class="summary-value" style="font-size: 16px;">${currencySymbol}${formData.netEosSettlement.toFixed(2)}</div>
          </div>
        </div>
        
        ${isEditMode ? `
        <div class="audit">
          <div class="section-title">Audit Trail</div>
          <div class="row">
            <div class="label">Created By:</div>
            <div class="value">${formData.createdBy?.userName || 'System'}</div>
          </div>
          ${formData.createdAt ? `
          <div class="row">
            <div class="label">Created At:</div>
            <div class="value">${dayjs(formData.createdAt).format('DD MMM YYYY, hh:mm A')}</div>
          </div>
          ` : ''}
          <div class="row">
            <div class="label">Last Updated By:</div>
            <div class="value">${formData.updatedBy?.userName || formData.createdBy?.userName || 'System'}</div>
          </div>
          ${formData.updatedAt ? `
          <div class="row">
            <div class="label">Updated At:</div>
            <div class="value">${dayjs(formData.updatedAt).format('DD MMM YYYY, hh:mm A')}</div>
          </div>
          ` : ''}
          ${formData.status === 'POSTED' ? `
          <div class="row">
            <div class="label">Posted By:</div>
            <div class="value">${formData.postedBy?.userName || 'System'}</div>
          </div>
          ${formData.postedDate ? `
          <div class="row">
            <div class="label">Posted At:</div>
            <div class="value">${dayjs(formData.postedDate).format('DD MMM YYYY, hh:mm A')}</div>
          </div>
          ` : ''}
          ` : ''}
        </div>
        ` : ''}
        
        <div class="footer">
          <p>This is a system-generated document. No signature required.</p>
          <p>Generated on: ${dayjs().format('DD MMM YYYY, hh:mm A')}</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const [formData, setFormData] = useState({
    empId: '',
    currentDate: dayjs().format('YYYY-MM-DD'),
    endDate: '',
    type: 'Termination',
    calculationBase: 1, // 1 = Allowances Include, 0 = Basic Only
    allowances: [],
    dayCount: 0,
    deductLoan: false,
    serviceYear: 0,
    serviceMonth: 0,
    unSettledLoan: 0,
    settlmentPerYear: 0,
    eosTotal: 0,
    netEosSettlement: 0,
    fromDate: '',
    toDate: '',
    leaveInfo: [],
    leaveCount: 0,
    note: '',
    status: 'DRAFT' // Default status for new records (matches backend DELIVERYSTAT.DRAFT)
  });

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load EOSS data if editing
  useEffect(() => {
    if (isEditMode && id) {
      loadEOSSData();
    } else {
      generateSequence();
    }
  }, [id]);

  // Calculate service years/months when employee or end date changes
  useEffect(() => {
    if (selectedEmployee?.date_of_join && formData.endDate) {
      calculateService();
    }
  }, [selectedEmployee, formData.endDate]);

  // Recalculate settlement when calculation base or allowances change
  useEffect(() => {
    if (selectedEmployee && formData.serviceYear > 0) {
            calculateSettlement(formData.serviceYear, formData.serviceMonth);
    }
  }, [formData.calculationBase, formData.allowances, formData.type]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Load employees
      await listAllEmployees({
        branchId: localStorage.getItem('branchId') || '',
        successCallback: (data) => {
              setEmployees(data || []);
        },
        errorCallback: (err) => console.error('Failed to load employees:', err)
      });

      // Load departments
      await getAllDepartmentsAPI({
        successCallback: (data) => setDepartments(data || []),
        errorCallback: (err) => console.error('Failed to load departments:', err)
      });

      // Load designations
      await getAllDesignationsAPI({
        successCallback: (data) => setDesignations(data || []),
        errorCallback: (err) => console.error('Failed to load designations:', err)
      });

      // Load allowances (pay heads with category ALLOWANCE)
      await listPayHeadApi({
        body: { page: 1, limit: 100, search: '', category: 'ALLOWANCE' },
        setPayHead: (data) => setAllowances(data || [])
      });
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSequence = async () => {
    try {
      await generateEOSSSequence({
        currentDate: formData.currentDate,
        successCallback: (seqNumber) => setSequence(seqNumber),
        errorCallback: (err) => console.error('Failed to generate sequence:', err)
      });
    } catch (error) {
      console.error('Error generating sequence:', error);
    }
  };

  const loadEOSSData = async (eossId) => {
    setLoading(true);
    try {
      await getEOSSById({
        id,
        successCallback: (data) => {
          console.log('🔍 Backend response data:', data);
          console.log('🔍 empId object:', data.empId);
          console.log('🔍 empId.monthlySalary:', data.empId?.monthlySalary);
          console.log('🔍 empId.designation:', data.empId?.designation);
          console.log('🔍 empId.department:', data.empId?.department);
          console.log('🔍 basicSalary fallback:', data.basicSalary);
          console.log('🔍 designationName fallback:', data.designationName);
          console.log('🔍 departmentName fallback:', data.departmentName);
          setSequence(data.sequence);
          setFormData({
            empId: data.empId?._id || '',
            currentDate: data.currentDate || '',
            endDate: data.endDate || '',
            type: data.type || 'Termination',
            calculationBase: data.calculationBase || 1,
            allowances: data.allowances || [],
            dayCount: data.dayCount || 0,
            deductLoan: data.deductLoan || false,
            serviceYear: data.serviceYear || 0,
            serviceMonth: data.serviceMonth || 0,
            unSettledLoan: data.unSettledLoan || 0,
            settlmentPerYear: data.settlmentPerYear || 0,
            eosTotal: data.eosTotal || 0,
            netEosSettlement: data.netEosSettlement || 0,
            fromDate: data.fromDate || '',
            toDate: data.toDate || '',
            leaveInfo: data.leaveInfo || [],
            leaveCount: data.leaveCount || 0,
            note: data.note || '',
            status: data.status || 'DRAFT', // IMPORTANT: Load status from backend
            // Audit trail fields
            createdBy: data.createdBy || null,
            createdAt: data.createdAt || null,
            updatedBy: data.updatedBy || null,
            updatedAt: data.updatedAt || null,
            postedBy: data.postedBy || null,
            postedDate: data.postedDate || null
          });
          // Set selected employee - use empId data if employees not loaded yet
          if (data.empId) {
            const employeeData = {
              _id: data.empId._id || data.empId,
              staff_name: data.empId.staff_name || data.employeeName || 'Unknown Employee',
              emp_id: data.empId.emp_id || data.employeeCode || 'N/A',
              date_of_join: data.empId.date_of_join || data.joiningDate,
              monthlySalary: data.empId.monthlySalary || data.basicSalary || 0,
              designation: {
                _id: data.empId.designation?._id,
                position: data.empId.designation?.position || data.designationName || 'N/A'
              },
              department: {
                _id: data.empId.department?._id,
                departmentName: data.empId.department?.departmentName || data.departmentName || 'N/A'
              }
            };
            setSelectedEmployee(employeeData);
            console.log('👤 Loaded employee data:', employeeData);
          }
        },
        errorCallback: (err) => {
          Swal.fire('Error', 'Failed to load EOSS data', 'error');
          navigate('/userdashboard/payroll/attendance/EossPayroll');
        }
      });
    } catch (error) {
      console.error('Error loading EOSS data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeChange = async (event, newValue) => {
    
    if (newValue) {
      // Duplicate guard: prevent creating a second EOSS for the same employee
      try {
        const res = await getEOSSList({ page: 1, limit: 1, empId: newValue._id });
        const existingList = res?.data?.data?.data || res?.data?.data || [];
        const existing = Array.isArray(existingList) ? existingList[0] : null;
        if (!isEditMode && existing) {
          const result = await Swal.fire({
            title: 'EOSS already exists',
            text: 'An End of Service Settlement already exists for this employee. Do you want to open it?',
            icon: 'info',
            showCancelButton: true,
            confirmButtonText: 'Open Existing',
            cancelButtonText: 'Choose Another Employee'
          });
          if (result.isConfirmed && existing?._id) {
            navigate(`/userdashboard/payroll/attendance/EossPayroll/Edit/${existing._id}`);
          }
          // Reset selection
          return;
        }
      } catch (e) {
        // Non-blocking: proceed if check fails
        console.warn('EOSS duplicate check failed:', e);
      }

      // Fetch FULL employee details from HRM employee master
      try {
        const employeeResponse = await axios.post('employee/getEmployeeById', { id: newValue._id });
        const fullEmployeeData = employeeResponse.data;
        
        console.log('👤 Full employee data from API:', fullEmployeeData);
        
        // Also fetch payroll data for designation/department
        await getEmployeeBasicDataAPI({
          body: { empId: newValue._id },
          successCallback: (payrollData) => {
            
            console.log('💼 Payroll data:', payrollData);
            
            // Extract salary from payroll data (check both actual payslip earnings and template)
            let salaryFromPayroll = 0;
            
            // First, check if there's actual payslip earnings data
            if (payrollData.earnings && Array.isArray(payrollData.earnings)) {
              const basicEarning = payrollData.earnings.find(
                e => e.name?.toLowerCase().includes('basic') || e.payHeadName?.toLowerCase().includes('basic')
              );
              salaryFromPayroll = basicEarning?.amount || basicEarning?.value || 0;
            }
            
            // If no payslip data, fallback to template
            if (!salaryFromPayroll && payrollData.tempData?.earnings) {
              const basicEarning = payrollData.tempData.earnings.find(
                e => e.name?.toLowerCase().includes('basic') || e.payHeadName?.toLowerCase().includes('basic')
              );
              salaryFromPayroll = basicEarning?.amount || basicEarning?.value || 0;
            }
            
            // Properly structure designation and department objects
            let designationObj = payrollData.designationData || fullEmployeeData?.designation || {};
            let departmentObj = payrollData.departmentData || fullEmployeeData?.department || {};

            // Fallback: resolve names from master lists when only IDs are present
            // Resolve designation
            if (!designationObj?.position) {
              const desigId = designationObj?._id || designationObj?.designationId || fullEmployeeData?.designation || newValue?.designation || null;
              if (desigId && Array.isArray(designations)) {
                const foundDesig = designations.find(d => String(d?._id) === String(desigId));
                if (foundDesig) {
                  designationObj = {
                    _id: foundDesig._id,
                    position: foundDesig.position || foundDesig.designationName || foundDesig.name || 'N/A'
                  };
                }
              }
            }

            // Resolve department
            if (!departmentObj?.departmentName) {
              const deptId = departmentObj?._id || departmentObj?.departmentId || fullEmployeeData?.department || newValue?.department || null;
              if (deptId && Array.isArray(departments)) {
                const foundDept = departments.find(d => String(d?._id) === String(deptId));
                if (foundDept) {
                  departmentObj = {
                    _id: foundDept._id,
                    departmentName: foundDept.departmentName || foundDept.name || 'N/A'
                  };
                }
              }
            }
            
            // Merge employee master data with payroll data
            const mergedData = {
              ...fullEmployeeData,
              // PRIORITY: Use employee's actual salary first, fallback to pay template if employee salary is 0
              monthlySalary: fullEmployeeData?.monthlySalary || fullEmployeeData?.basicSalary || fullEmployeeData?.salary || salaryFromPayroll || 0,
              date_of_join: fullEmployeeData?.date_of_join || fullEmployeeData?.joiningDate || fullEmployeeData?.dateOfJoin || fullEmployeeData?.joining_date,
              designation: {
                _id: designationObj?._id || designationObj?.designationId || null,
                position: designationObj?.position || designationObj?.designationName || designationObj?.name || 'N/A'
              },
              department: {
                _id: departmentObj?._id || departmentObj?.departmentId || null,
                departmentName: departmentObj?.departmentName || departmentObj?.name || 'N/A'
              }
            };
            
            console.log('✅ Merged employee data:', mergedData);
            setSelectedEmployee(mergedData);
            
            setFormData(prev => ({
              ...prev,
              empId: newValue._id,
              fromDate: mergedData.date_of_join ? dayjs(mergedData.date_of_join).format('YYYY-MM-DD') : '',
              // Reset calculations when employee changes
              serviceYear: 0,
              serviceMonth: 0,
              dayCount: 0,
              settlmentPerYear: 0,
              eosTotal: 0,
              netEosSettlement: 0
            }));
          },
          errorCallback: (err) => {
            console.error('❌ Failed to load payroll details:', err);
            // Use employee master data only
            const mergedData = {
              ...fullEmployeeData,
              monthlySalary: fullEmployeeData?.monthlySalary || fullEmployeeData?.basicSalary || fullEmployeeData?.salary || 0,
              date_of_join: fullEmployeeData?.date_of_join || fullEmployeeData?.joiningDate || fullEmployeeData?.dateOfJoin || fullEmployeeData?.joining_date
            };
            
            setSelectedEmployee(mergedData);
            setFormData(prev => ({
              ...prev,
              empId: newValue._id,
              fromDate: mergedData.date_of_join ? dayjs(mergedData.date_of_join).format('YYYY-MM-DD') : '',
              serviceYear: 0,
              serviceMonth: 0,
              dayCount: 0,
              settlmentPerYear: 0,
              eosTotal: 0,
              netEosSettlement: 0
            }));
          }
        });
      } catch (error) {
        console.error('❌ Error fetching full employee details:', error);
        Swal.fire('Error', 'Failed to load employee details', 'error');
      }
    } else {
      setSelectedEmployee(null);
      setFormData(prev => ({ 
        ...prev, 
        empId: '', 
        fromDate: '',
        serviceYear: 0,
        serviceMonth: 0,
        dayCount: 0,
        settlmentPerYear: 0,
        eosTotal: 0,
        netEosSettlement: 0
      }));
    }
  };

  const calculateService = () => {
    const joinDate = dayjs(selectedEmployee.date_of_join);
    const endDate = dayjs(formData.endDate);
    
    if (joinDate.isValid() && endDate.isValid()) {
      const years = endDate.diff(joinDate, 'year');
      const months = endDate.diff(joinDate.add(years, 'year'), 'month');
      const days = endDate.diff(joinDate.add(years, 'year').add(months, 'month'), 'day');


      setFormData(prev => ({
        ...prev,
        serviceYear: years,
        serviceMonth: months,
        dayCount: days,
        fromDate: joinDate.format('YYYY-MM-DD'),
        toDate: endDate.format('YYYY-MM-DD')
      }));

      // Calculate settlement based on type and service
      calculateSettlement(years, months);
    }
  };

  const calculateSettlement = (years, months) => {
    if (!selectedEmployee?.monthlySalary) {
      console.warn('⚠️ Cannot calculate settlement: No monthly salary');
      return;
    }

    let settlementPerYear = 0;
    const basicSalary = selectedEmployee.monthlySalary;
    const totalAllowances = formData.calculationBase === 1 
      ? formData.allowances.reduce((sum, all) => sum + (all.amount || 0), 0)
      : 0;
    const totalSalary = basicSalary + totalAllowances;
    
    // Convert months to fractional years for accurate calculation
    const totalYears = years + (months / 12);
    
    console.log('💰 Settlement Calculation:', {
      basicSalary,
      totalAllowances,
      totalSalary,
      years,
      months,
      totalYears: totalYears.toFixed(2),
      calculationBase: formData.calculationBase,
      allowancesCount: formData.allowances.length
    });

    // Settlement calculation based on type
    let totalSettlement = 0;
    switch (formData.type) {
      case 'Termination':
        // Termination: 21 days per year for first 5 years, 30 days after
        if (totalYears <= 5) {
          totalSettlement = (totalSalary / 30) * 21 * totalYears;
          settlementPerYear = (totalSalary / 30) * 21;
        } else {
          const first5Years = (totalSalary / 30) * 21 * 5;
          const remainingYears = (totalSalary / 30) * 30 * (totalYears - 5);
          totalSettlement = first5Years + remainingYears;
          settlementPerYear = totalSettlement / totalYears;
        }
        break;
      case 'Resignation':
        // Resignation: 21 days per year (less than 5 years get reduced)
        if (totalYears < 1) {
          totalSettlement = 0;
          settlementPerYear = 0;
        } else if (totalYears < 5) {
          totalSettlement = (totalSalary / 30) * 21 * totalYears * 0.5; // 50% for < 5 years
          settlementPerYear = (totalSalary / 30) * 21 * 0.5;
        } else {
          totalSettlement = (totalSalary / 30) * 21 * totalYears;
          settlementPerYear = (totalSalary / 30) * 21;
        }
        break;
      case 'Retirement':
        // Retirement: Full 30 days per year
        totalSettlement = (totalSalary / 30) * 30 * totalYears;
        settlementPerYear = (totalSalary / 30) * 30;
        break;
      default:
        totalSettlement = 0;
        settlementPerYear = 0;
    }

    const netSettlement = totalSettlement - formData.unSettledLoan;

    setFormData(prev => ({
      ...prev,
      settlmentPerYear: Math.round(settlementPerYear * 100) / 100,
      eosTotal: Math.round(totalSettlement * 100) / 100,
      netEosSettlement: Math.round(netSettlement * 100) / 100
    }));
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const tempErrors = {};
    if (!formData.empId) tempErrors.empId = 'Employee is required';
    if (!selectedEmployee?.monthlySalary || selectedEmployee.monthlySalary === 0) {
      tempErrors.empId = 'Employee must have a valid monthly salary';
    }
    if (!formData.currentDate) tempErrors.currentDate = 'Date is required';
    if (!formData.endDate) tempErrors.endDate = 'End date is required';
    if (!formData.type) tempErrors.type = 'EOSS type is required';
    
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      Swal.fire('Validation Error', 'Please fill all required fields', 'warning');
      return;
    }

    setSaving(true);
    
    const payload = {
      ...formData,
      allowances: formData.allowances.map(a => ({ alWncId: a._id || a.alWncId }))
    };

    
    try {
      if (isEditMode) {
        await updateEOSS({
          body: { ...payload, _id: id },
          successCallback: () => {
            Swal.fire('Success', 'EOSS updated successfully', 'success');
            navigate('/userdashboard/payroll/attendance/EossPayroll');
          },
          errorCallback: (err) => {
            Swal.fire('Error', err.message || 'Failed to update EOSS', 'error');
          }
        });
      } else {
        await createEOSS({
          body: payload,
          successCallback: () => {
            Swal.fire('Success', 'EOSS created successfully', 'success');
            navigate('/userdashboard/payroll/attendance/EossPayroll');
          },
          errorCallback: (err) => {
            Swal.fire('Error', err.message || 'Failed to create EOSS', 'error');
          }
        });
      }
    } catch (error) {
      console.error('Error saving EOSS:', error);
      Swal.fire('Error', 'An unexpected error occurred', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    // Only allow delete for DRAFT status
    if (formData.status !== 'DRAFT') {
      Swal.fire('Error', 'Only Draft EOSS records can be deleted', 'error');
      return;
    }

    const result = await Swal.fire({
      title: 'Delete EOSS?',
      text: 'This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await deleteEOSS({
          body: { _id: id },
          successCallback: () => {
            Swal.fire('Deleted!', 'EOSS record has been deleted', 'success');
            navigate('/userdashboard/payroll/attendance/EossPayroll');
          },
          errorCallback: (err) => {
            Swal.fire('Error', err.message || 'Failed to delete EOSS', 'error');
          }
        });
      } catch (error) {
        console.error('Error deleting EOSS:', error);
        Swal.fire('Error', 'An unexpected error occurred', 'error');
      }
    }
  };

  const handlePost = async () => {
    // Only allow post for DRAFT status
    if (formData.status !== 'DRAFT') {
      Swal.fire('Error', 'Only Draft EOSS records can be posted', 'error');
      return;
    }

    const result = await Swal.fire({
      title: 'Post EOSS?',
      text: 'Once posted, this record cannot be edited or deleted',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, post it!'
    });

    if (result.isConfirmed) {
      try {
        await postEOSS({
          body: { _id: id },
          successCallback: () => {
            Swal.fire('Posted!', 'EOSS posted successfully', 'success');
            navigate('/userdashboard/payroll/attendance/EossPayroll');
          },
          errorCallback: (err) => {
            Swal.fire('Error', err.message || 'Failed to post EOSS', 'error');
          }
        });
      } catch (error) {
        console.error('Error posting EOSS:', error);
        Swal.fire('Error', 'An unexpected error occurred', 'error');
      }
    }
  };

  const handleBackBtn = () => {
    navigate("/userdashboard/payroll/attendance/EossPayroll");
  };

  const eosTypeOptions = ['Termination', 'Resignation', 'Retirement'];

  if (loading) {
    return (
      <Pagelayout handleBack={handleBackBtn} pageName={isEditMode ? "Edit EOSS" : "Create EOSS"} headerLayout={2}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Pagelayout>
    );
  }

  return (
    <Pagelayout 
      handleBack={handleBackBtn} 
      pageName={isEditMode ? "Edit EOSS" : "Create EOSS"} 
      headerLayout={2} 
      newData={!isEditMode} 
      handleSave={handleSubmit}
      saveLoading={saving}
      status={formData?.status}
      editBtn={false}
      headerRightExtra={selectedEmployee ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <EmployeeImage 
            employeeId={selectedEmployee?._id}
            employeeCode={selectedEmployee?.emp_id}
            empId={selectedEmployee?._id}
            employeeName={selectedEmployee?.staff_name}
            size={32}
          />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {`${String(selectedEmployee?.emp_id || 'N/A')} - ${selectedEmployee?.staff_name || 'Unknown'}`}
          </Typography>
        </Stack>
      ) : null}
    >
      <div>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
            {sequence || 'Generating...'}
          </Typography>
          <Chip 
            label={isEditMode ? 'Edit Mode' : 'New Record'} 
            color={isEditMode ? 'info' : 'success'}
            size="small"
          />
        </Box>
        
        <Grid container spacing={3}>
          {/* Employee Selection */}
          <Grid item xs={12} md={8}>
            <Autocomplete
              fullWidth
              size="small"
              options={employees}
              getOptionLabel={(option) => {
                // Try multiple fields for employee code - convert to string
                const empCode = String(option.emp_id || option.empId || option.employeeId || option.code || 'N/A');
                const empName = option.staff_name || option.name || option.employeeName || 'Unknown';
                return `${empCode} - ${empName}`;
              }}
              value={selectedEmployee}
              isOptionEqualToValue={(option, value) => option?._id === value?._id}
              onChange={handleEmployeeChange}
              disabled={isEditMode}
              renderOption={(props, option) => {
                const empCode = String(option.emp_id || option.empId || option.employeeId || option.code || 'N/A');
                const empName = option.staff_name || option.name || option.employeeName || 'Unknown';
                return (
                  <li {...props} key={option._id}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {empCode}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {empName}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => {
                const empCode = String(selectedEmployee?.emp_id || '');
                const empName = selectedEmployee?.staff_name || '';
                const fullLabel = selectedEmployee ? `${empCode} - ${empName}` : '';
                return (
                  <TextField
                    {...params}
                    label="Employee"
                    placeholder="Select Employee"
                    error={!!errors.empId}
                    inputProps={{
                      ...params.inputProps,
                      title: fullLabel,
                    }}
                    InputProps={{
                      ...params.InputProps,
                      style: { fontSize: '14px' }
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        textOverflow: 'clip'
                      }
                    }}
                    helperText={errors.empId}
                    required
                    InputLabelProps={{ shrink: true }}
                  />
                );
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              size="small"
              fullWidth
              label="Current Date"
              name="currentDate"
              type="date"
              value={formData.currentDate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>

          {/* Employee Details Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
              Employee Details
            </Typography>
          </Grid>

          {selectedEmployee && (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  size="small"
                  fullWidth
                  label="Designation"
                  value={
                    selectedEmployee?.designation?.position ||
                    selectedEmployee?.designation?.designationName ||
                    selectedEmployee?.designation?.name ||
                    selectedEmployee?.designationData?.position ||
                    selectedEmployee?.designationData?.designationName ||
                    selectedEmployee?.designationData?.name ||
                    'N/A'
                  }
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  size="small"
                  fullWidth
                  label="Department"
                  value={
                    selectedEmployee?.department?.departmentName ||
                    selectedEmployee?.department?.name ||
                    selectedEmployee?.departmentData?.departmentName ||
                    selectedEmployee?.departmentData?.name ||
                    'N/A'
                  }
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  size="small"
                  fullWidth
                  label="Joining Date"
                  value={
                    (() => {
                      const doj = selectedEmployee?.date_of_join ||
                                   selectedEmployee?.joiningDate ||
                                   selectedEmployee?.dateOfJoin ||
                                   selectedEmployee?.join_date ||
                                   selectedEmployee?.hireDate;
                      return doj ? dayjs(doj).format('YYYY-MM-DD') : '';
                    })()
                  }
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  size="small"
                  fullWidth
                  label="Basic Salary"
                  value={
                    selectedEmployee?.monthlySalary ||
                    selectedEmployee?.basicSalary ||
                    selectedEmployee?.salary ||
                    selectedEmployee?.grossSalary ||
                    selectedEmployee?.netSalary ||
                    0
                  }
                  InputProps={{ readOnly: true, startAdornment: <Typography sx={{ mr: 1 }}>{currencySymbol}</Typography> }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="End Date"
              name="endDate"
              type="date"
              size="small"
              value={formData.endDate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              required
              error={!!errors.endDate}
              helperText={errors.endDate}
            />
          </Grid>

          {/* End of Service Details */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
              End of Service Details
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              size="small"
              fullWidth
              label="EOSS Type"
              name="type"
              value={formData.type}
              onChange={handleChange}
              required
              error={!!errors.type}
              helperText={errors.type}
              InputLabelProps={{ shrink: true }}
            >
              {eosTypeOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              size="small"
              fullWidth
              label="Calculation Base"
              name="calculationBase"
              value={formData.calculationBase}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value={1}>Allowances Include</MenuItem>
              <MenuItem value={0}>Basic Only</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              size="small"
              multiple
              options={allowances}
              getOptionLabel={(option) => `${option.name || ''} (${currencySymbol}${option.amount || 0})`}
              value={formData.allowances}
              onChange={(_, newValue) => {
                // Store both ID and amount for calculation
                const allowancesWithAmounts = newValue.map(alw => ({
                  _id: alw._id,
                  alWncId: alw._id,
                  name: alw.name,
                  amount: alw.amount || 0
                }));
                setFormData(prev => ({ ...prev, allowances: allowancesWithAmounts }));
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      variant="outlined"
                      label={`${option.name} (${currencySymbol}${option.amount || 0})`}
                      size="small"
                      {...tagProps}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Included Allowances"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>

          {/* Service Calculation Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
              Service Calculation
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Service Years"
              value={formData.serviceYear}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Service Months"
              value={formData.serviceMonth}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Service Days"
              value={formData.dayCount}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Loan & Settlement Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
              Loan & Settlement
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              size="small"
              label="Deduct Loan"
              name="deductLoan"
              value={formData.deductLoan}
              onChange={(e) => setFormData(prev => ({ ...prev, deductLoan: e.target.value === 'true' }))}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value={false}>No</MenuItem>
              <MenuItem value={true}>Yes</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Unsettled Loan"
              name="unSettledLoan"
              type="number"
              value={formData.unSettledLoan}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>{currencySymbol}</Typography>,
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Settlement Per Year"
              value={formData.settlmentPerYear}
              InputProps={{ 
                readOnly: true,
                startAdornment: <Typography sx={{ mr: 1 }}>{currencySymbol}</Typography>,
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ 
                '& .MuiInputBase-input': { 
                  fontWeight: 600, 
                  color: 'success.main' 
                } 
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Total Settlement"
              value={Math.round((formData.settlmentPerYear * formData.serviceYear) * 100) / 100}
              InputProps={{ 
                readOnly: true,
                startAdornment: <Typography sx={{ mr: 1 }}>{currencySymbol}</Typography>,
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ 
                '& .MuiInputBase-input': { 
                  fontWeight: 600, 
                  color: 'primary.main',
                  fontSize: '1.1rem'
                } 
              }}
            />
          </Grid>

          {/* Notes Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
              Additional Information
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Notes"
              name="note"
              value={formData.note}
              onChange={handleChange}
              multiline
              rows={3}
              InputLabelProps={{ shrink: true }}
              placeholder="Add any additional notes or comments..."
            />
          </Grid>

          {/* Settlement Details */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="EOS Total"
              name="eosTotal"
              value={formData.eosTotal}
              onChange={handleChange}
              error={!!errors.eosTotal}
              helperText={errors.eosTotal}
              required
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>{currencySymbol}</Typography>,
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Net EOS Settlement"
              name="netEosSettlement"
              value={formData.netEosSettlement}
              onChange={handleChange}
              error={!!errors.netEosSettlement}
              helperText={errors.netEosSettlement}
              required
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>{currencySymbol}</Typography>,
              }}
            />
          </Grid>
        </Grid>

        {/* Summary Card */}
        <Paper 
          elevation={3} 
          sx={{ 
            p: 3, 
            mt: 3, 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Settlement Summary
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Service Period</Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {formData.serviceYear}y {formData.serviceMonth}m
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Per Year</Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {currencySymbol}{formData.settlmentPerYear.toFixed(2)}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Total Settlement</Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {currencySymbol}{((formData.settlmentPerYear * formData.serviceYear)).toFixed(2)}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Net Amount</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {currencySymbol}{((formData.settlmentPerYear * formData.serviceYear) - formData.unSettledLoan).toFixed(2)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Action Buttons for Edit Mode */}
        {isEditMode && formData.status === 'DRAFT' && (
          <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              sx={{ minWidth: 140 }}
            >
              Save
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={handleDelete}
              sx={{ minWidth: 120 }}
            >
              Delete
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handlePost}
              sx={{ minWidth: 120 }}
            >
              Post
            </Button>
          </Box>
        )}

        {/* Status Info for Posted Records */}
        {isEditMode && formData.status !== 'DRAFT' && (
          <Alert severity="info" sx={{ mt: 3 }}>
            This EOSS record has been posted and cannot be edited or deleted.
          </Alert>
        )}
      </Paper>
      
      {/* Audit Trail Section - Only in Edit Mode */}
      {isEditMode && (
        <Paper sx={{ p: 3, mt: 2 }} className="print-break">
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
            Audit Trail
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Created By
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {formData.createdBy?.userName || 'System'}
              </Typography>
              {formData.createdAt && (
                <Typography variant="caption" color="text.secondary">
                  {dayjs(formData.createdAt).format('DD MMM YYYY, hh:mm A')}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Last Updated By
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {formData.updatedBy?.userName || formData.createdBy?.userName || 'System'}
              </Typography>
              {formData.updatedAt && (
                <Typography variant="caption" color="text.secondary">
                  {dayjs(formData.updatedAt).format('DD MMM YYYY, hh:mm A')}
                </Typography>
              )}
            </Grid>
            {formData.status === 'POSTED' && (
              <>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Posted By
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formData.postedBy?.userName || 'System'}
                  </Typography>
                  {formData.postedDate && (
                    <Typography variant="caption" color="text.secondary">
                      {dayjs(formData.postedDate).format('DD MMM YYYY, hh:mm A')}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Posting Date
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formData.postedDate ? dayjs(formData.postedDate).format('DD MMM YYYY') : 'N/A'}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
        </Paper>
      )}
      
      {/* Print Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }} className="no-print">
        <Button
          variant="outlined"
          color="primary"
          onClick={handlePrint}
          startIcon={<span>🖨️</span>}
        >
          Print EOSS
        </Button>
      </Box>
    </div>
    </Pagelayout>
  );
};

export default CreateEoss;